/**
 * useAuthContext — hook que hidrata el contexto del usuario llamando a
 * tax-account /api/v1/auth/me.
 *
 * Razón: con el slim JWT (sprint I.0a), el JWT solo contiene
 * sub/jti/iss/aud/exp/iat. Email, active_org, role, memberships viven en
 * tax-account y se resuelven vía API.
 *
 * Cache: TanStack Query con staleTime=60s (alineado con el cache Redis
 * que tiene tax-account en backend, evita stampede). refetchOnWindowFocus
 * habilitado para reflejar cambios de switch-org sin reload manual.
 *
 * Si el JWT expiró o no hay sesión, la query falla con 401 y se
 * recomienda al caller redirigir a /login.
 */

import { useQuery } from '@tanstack/react-query'
import { accountApi, getAccessToken } from '@/lib/api'

// ─── Tipos del payload de /auth/me ─────────────────────────────────

export interface MeOrganization {
  id: string
  tax_id: string
  razon_social: string | null
  nombre_comercial: string | null
  entity_type: string | null
  is_active: boolean
}

export interface MeActiveOrg {
  id: string
  tax_id: string
  razon_social: string | null
  nombre_comercial: string | null
  entity_type: string | null
  role: string // 'admin' | 'editor' | 'viewer' | etc.
}

export interface MeMembership {
  membership_id: string
  organization_id: string
  role: string
  status: string
  is_active_session_org: boolean
  organization: MeOrganization | null
}

export interface MeResponse {
  person_id: string
  session_id: string
  email: string
  first_name: string | null
  last_name: string | null
  full_name: string
  initials: string
  avatar_url: string | null
  email_verified: boolean
  verification_level: number
  totp_enabled: boolean
  active_org: MeActiveOrg | null
  memberships_count: number
  memberships: MeMembership[]
}

// ─── Hook ──────────────────────────────────────────────────────────

const ME_QUERY_KEY = ['auth', 'me'] as const

/**
 * useAuthContext — devuelve el contexto del usuario actual.
 *
 * `data` puede ser undefined (loading) o el MeResponse completo.
 * `activeOrgId` es un atajo a `data?.active_org?.id ?? null`.
 * `isAdmin` resuelve el rol del active_org case-insensitive.
 *
 * El hook está diseñado para usarse en componentes — los queries que
 * dependen del orgId se gateaban antes con `enabled: !!orgId`. Ahora
 * deben gatearse con `enabled: !!activeOrgId` (que sigue siendo null
 * mientras carga).
 */
export function useAuthContext() {
  const enabled = !!getAccessToken()

  const query = useQuery<MeResponse>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      const r = await accountApi.get<MeResponse>('/api/v1/auth/me')
      return r.data
    },
    enabled,
    staleTime: 60 * 1000,           // 60s — alineado con cache Redis backend
    refetchOnWindowFocus: true,     // detecta switch-org sin reload manual
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      // No reintentar 401 — JWT expirado, dejar que el caller redirija.
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 401) return false
      return failureCount < 2
    },
  })

  const data = query.data
  const activeOrgId = data?.active_org?.id ?? null
  const isAdmin =
    data?.active_org?.role !== undefined &&
    data.active_org.role.toLowerCase() === 'admin'

  // needsCompanyRegistration: cuando el person está autenticado pero
  // NO tiene memberships activos. El frontend usa este flag para
  // redirigir a /registrar-empresa antes de cargar dashboard/settings/etc.
  const needsCompanyRegistration =
    !!data && (data.memberships_count ?? 0) === 0

  return {
    ...query,
    me: data,
    activeOrgId,
    activeOrgRole: data?.active_org?.role ?? null,
    isAdmin,
    needsCompanyRegistration,
    email: data?.email ?? null,
    fullName: data?.full_name ?? null,
    initials: data?.initials ?? null,
    memberships: data?.memberships ?? [],
  }
}

/**
 * QueryKey export para que llamadas de switch-org/logout puedan
 * invalidar la cache desde fuera del hook.
 *
 * Uso típico:
 *   const qc = useQueryClient()
 *   await accountApi.post('/api/v1/auth/switch-org', { ... })
 *   await qc.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
 */
export const AUTH_ME_QUERY_KEY = ME_QUERY_KEY
